import { MUICheckBox, MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import {
  createFactoryValidationSchemas,
  useFactoryCheckboxHelper,
} from "../../../../components/Helpers"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import { FactoryGet, FactoryPut, FactoryTypeEnum } from "../../../../models/Factory"
import { UserInfo } from "../../../../models/User"

const { FactoryTypeSchema, validationSchema } = createFactoryValidationSchemas()

export function FactoryDetails(props: {
  readonly factory: FactoryGet
  readonly user: UserInfo
  readonly setFactory: (updatedFactory: FactoryGet) => void
}) {
  const { factory, user, setFactory } = props

  const returnURL = `/factory-reports#factories`

  const formMethods = useForm<FactoryPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: factory.name,
      type: factory.type,
      isFree: factory.isFree || false,
      isEnabled: factory.isEnabled || false,
      maxDays: factory.maxDays,
    })
  }, [factory, reset])

  const onSubmit = useCallback(
    async (values: FactoryPut) => {
      try {
        const res = await digitalManagerApi.put<FactoryGet>(`/factories/${factory.id}`, {
          name: values.name,
          type: values.type,
          isFree: values.isFree,
          isEnabled: values.isEnabled,
          maxDays: values.maxDays,
        })
        toast.success("Factory updated")
        setFactory(res.data)
      } catch {
        toast.error("Cannot update factory")
      }
    },
    [factory.id, setFactory]
  )

  const handleParsedNumberChange = useCallback(
    (field: ControllerRenderProps<FactoryPut, "maxDays">) => (value: string) => {
      const parsed = value ? Number.parseInt(value, 10) : ""
      field.onChange(parsed)
    },
    []
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<FactoryPut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<FactoryPut, "name"> }) => (
      <MUITextField
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const handleTypeSelectChange = useCallback(
    (field: ControllerRenderProps<FactoryPut, "type">) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : first?.id ?? ""
        field.onChange(value)
      },
    []
  )

  const renderTypeSelect = useCallback(
    ({ field }: { field: ControllerRenderProps<FactoryPut, "type"> }) => (
      <MUISelect
        name={field.name}
        id="type"
        size={32}
        dropdownLOV={Object.entries(FactoryTypeEnum).map(([key, value]) => ({
          id: String(key),
          name: String(value),
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleTypeSelectChange(field)}
        onBlur={field.onBlur}
      />
    ),
    [handleTypeSelectChange]
  )

  const { handleBooleanToggle, renderCheckboxIsFree } =
    useFactoryCheckboxHelper<FactoryPut>({
      renderCheckbox: (field, handleToggle) => (
        <MUICheckBox {...field} checked={field.value} handleToggle={handleToggle} />
      ),
    })

  const renderCheckboxIsEnabled = useCallback(
    ({ field }: { field: ControllerRenderProps<FactoryPut, "isEnabled"> }) => (
      <MUICheckBox
        {...field}
        checked={field.value}
        handleToggle={handleBooleanToggle(field)}
      />
    ),
    [handleBooleanToggle]
  )

  const renderMaxDaysTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<FactoryPut, "maxDays"> }) => (
      <MUITextField
        id="maxDays"
        type="number"
        size={32}
        name={field.name}
        value={
          field.value !== undefined && field.value !== null ? String(field.value) : ""
        }
        onChange={handleParsedNumberChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.maxDays?.message === "string"
            ? { maxDays: { message: errors.maxDays.message } }
            : undefined
        }
      />
    ),
    [errors.maxDays, handleParsedNumberChange]
  )

  if (!factory) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<FactoryPut> = {
    formMethods,
    canWrite: hasUserAccessToFeature(
      user.authorizations,
      DimaFeatureShortNameEnum.FR,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{factory.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderNameTextField} />
        ),
      },
      {
        id: 1,
        label: "Type",
        displayItem: (
          <div className="title">
            {(FactoryTypeEnum as Record<string, FactoryTypeEnum>)[factory.type]}
          </div>
        ),
        editItem: <Controller name="type" control={control} render={renderTypeSelect} />,
      },
      {
        id: 2,
        label: "Is Free",
        displayItem: <div className="title">{factory.isFree?.toString() || "false"}</div>,
        editItem: (
          <Controller name="isFree" control={control} render={renderCheckboxIsFree} />
        ),
      },
      {
        id: 3,
        label: "Is Enabled",
        displayItem: (
          <div className="title">{factory.isEnabled?.toString() || "false"}</div>
        ),
        editItem: (
          <Controller
            name="isEnabled"
            control={control}
            render={renderCheckboxIsEnabled}
          />
        ),
      },
      {
        id: 4,
        label: "Max Days",
        displayItem: <div className="title">{factory.maxDays}</div>,
        editItem: (
          <Controller name="maxDays" control={control} render={renderMaxDaysTextField} />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
